import {
  Button,
  FilterDropdown,
  fontStyles,
  Icon,
  LoadingIndicator,
  Toggle,
  Tooltip,
} from '@monash/portal-react';
import c from './services.module.scss';
import { useContext, useEffect, useMemo, useState } from 'react';
import PersonalisationBanner from './components/personalisation-banner/PersonalisationBanner';
import PersonalisationModal from './components/personalisation-modal/PersonalisationModal';
import { DataContext } from '@/components/providers/data-provider/DataProvider';
import SearchBar from './components/search-bar/SearchBar';
import { SearchContextHoneycomb } from '@/components/providers/search-provider-honeycomb/SearchProviderHoneycomb';
import SearchError from '../search/SearchError';
import LinkSearchResultCardAdapter from './components/search-result-entity/adapters/LinkSearchResultCardAdapter';
import ImageSearchResultCardAdapter from './components/search-result-entity/adapters/ImageSearchResultCardAdapter';
import {
  byAlphabetical,
  isLinkType,
  byReverseAlphabetical,
  pairLinks,
  filterLinksByCategory,
  byRelevance,
  buildPersonalisationTally,
  enrichWithPersonalisationRelevance,
} from './utils';
import classNames from 'classnames';
import {
  AccessibilityContext,
  fsUpdateDoc,
  getAllCategories,
  ImpersonationContext,
  useApi,
} from '@monash/portal-frontend-common';
import { getSearchResultLiveMsg } from '../search/search-result-utils';
import * as Sentry from '@sentry/browser';
import { PageContext } from '@/components/providers/page-provider/PageProvider';
import {
  buildPersonaCombination,
  trackExploreFilterClick,
  trackExplorePageView,
  trackPersonalisationLinkClick,
  trackUpdatePersonalisationClick,
} from '@/utils/analytics';

export const LINK_TYPE = {
  LINK: 'link',
  SERVICE: 'service',
  EXPERIENCE: 'experience',
};

const FILTER_TOGGLE_OPTIONS = [
  {
    value: 'All links',
    type: null,
  },
  {
    value: 'Services',
    type: LINK_TYPE.SERVICE,
  },
  {
    value: 'Experiences',
    type: LINK_TYPE.EXPERIENCE,
  },
];

const SORT_TYPE = {
  MOST_RELEVANT: 'MOST_RELEVANT',
  ALPHABETICAL: 'ALPHABETICAL',
  REVERSE_ALPHABETICAL: 'REVERSE_ALPHABETICAL',
};

const SORT_OPTIONS = [
  {
    text: 'Most relevant',
    value: SORT_TYPE.MOST_RELEVANT,
  },
  {
    text: 'A - Z',
    value: SORT_TYPE.ALPHABETICAL,
  },
  {
    text: 'Z - A',
    value: SORT_TYPE.REVERSE_ALPHABETICAL,
  },
];

const Services = () => {
  const { setAppLivePoliteMsg } = useContext(AccessibilityContext);
  const { backgroundStyle, hasUploadedWallpaper } = useContext(PageContext);

  const {
    runSearchCallCount,
    loadingLinks,
    searchTarget,
    searchResult,
    searchError,
    runSearch,
    categories,
    setCategories,
  } = useContext(SearchContextHoneycomb);

  const { portalPreferences, setPortalPreferences } = useContext(DataContext);
  const { currentUser } = useContext(ImpersonationContext);
  const userDocPath = `users/${currentUser.uid}`;

  const linksPersonalisationPreferences =
    portalPreferences?.linksPersonalisation;
  const bannerIsHidden = linksPersonalisationPreferences?.banner?.isHidden;
  const personalisationResponses =
    linksPersonalisationPreferences?.questionResponses;
  const showPersonalisationBanner =
    !bannerIsHidden && !personalisationResponses;

  useEffect(() => {
    runSearch();
  }, []);

  const personaCombination = useMemo(
    () => buildPersonaCombination(personalisationResponses),
    [personalisationResponses]
  );

  useEffect(() => {
    trackExplorePageView();
  }, []);

  useApi({
    requestFn: getAllCategories,
    onSuccess: (response) => {
      setCategories(response);
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Categories data has failed to load.');
    },
  });

  const [filterToggleValue, setFilterToggleValue] = useState(
    FILTER_TOGGLE_OPTIONS[0]
  );

  const [categoryFilter, setCategoryFilter] = useState([]);
  const [sortType, setSortType] = useState(SORT_TYPE.MOST_RELEVANT);

  const enrichedSearchResult = useMemo(() => {
    if (!searchResult) return;
    let final = searchResult;

    if (personalisationResponses) {
      const personalisationTally = buildPersonalisationTally(
        personalisationResponses
      );
      final = final.map(
        enrichWithPersonalisationRelevance(personalisationTally)
      );
    }

    return final;
  }, [searchResult, personalisationResponses]);

  const filteredSortedSearchResult = useMemo(() => {
    if (!enrichedSearchResult) return [];
    let final = enrichedSearchResult;

    // Filter
    if (filterToggleValue?.type) {
      final = final.filter(isLinkType(filterToggleValue.type));
    }

    if (categoryFilter.length > 0) {
      final = filterLinksByCategory(categoryFilter, final);
    }

    // Sort
    if (sortType === SORT_TYPE.MOST_RELEVANT) {
      final = final.toSorted(byRelevance);
    } else if (sortType === SORT_TYPE.ALPHABETICAL) {
      final = final.toSorted(byAlphabetical);
    } else if (sortType === SORT_TYPE.REVERSE_ALPHABETICAL) {
      final = final.toSorted(byReverseAlphabetical);
    }

    return final;
  }, [enrichedSearchResult, filterToggleValue, categoryFilter, sortType]);

  const renderableSearchResults = useMemo(() => {
    return pairLinks(filteredSortedSearchResult);
  }, [filteredSortedSearchResult]);

  const searchResultCount = filteredSortedSearchResult?.length || 0;

  // Note: have a short dependency list to make sure live msg is only updated when we need to
  // - loadingLinks: to help reset live region when same search executed continuously
  // - runSearchCallCount: to only update live region when each search API call is resolved
  useEffect(() => {
    setAppLivePoliteMsg(
      getSearchResultLiveMsg({
        isLoading: loadingLinks,
        query: searchTarget,
        error: searchError,
        resultCount: searchResult?.length,
      })
    );
    // AX requirement:
    // - when each search call finalised, try focus back onto the search input
    // - so the next 'tab' key navigation will go to 'skip to search result',
    // - which was lost due to mounting the search result page into the DOM freshly each time
    if (!loadingLinks) {
      const searchInputNode = document.querySelector(
        '#servicesPageContainer input#search-input[role="searchbox"]'
      );
      searchInputNode && searchInputNode.focus();
    }
  }, [loadingLinks, runSearchCallCount]);

  const [showPersonalisationModal, setShowPersonalisationModal] =
    useState(false);
  const [isPersonalisationUpdate, setIsPersonalisationUpdate] = useState(false);

  const openPersonalisationModal = () => {
    setIsPersonalisationUpdate(false);
    setShowPersonalisationModal(true);
  };

  const handleClosePersonalisationModal = () => {
    setShowPersonalisationModal(false);
    setIsPersonalisationUpdate(false);
  };

  const handleUpdatePersonalisationClick = () => {
    trackUpdatePersonalisationClick({
      personaCombination,
      clickedAt: new Date().toISOString(),
    });
    setIsPersonalisationUpdate(true);
    setShowPersonalisationModal(true);
  };

  const handleShowBanner = async () => {
    const update = {
      isHidden: false,
      updatedAt: Date.now(),
    };

    // Optimistically update
    setPortalPreferences((prev) => ({
      ...prev,
      linksPersonalisation: {
        ...(prev.linksPersonalisation ?? {}),
        banner: {
          ...(prev.linksPersonalisation?.banner ?? {}),
          ...update,
        },
      },
    }));

    await fsUpdateDoc(userDocPath, {
      'preferences.linksPersonalisation.banner': update,
    });
  };

  const handleFilterToggleChange = (value) => {
    trackExploreFilterClick({
      filterValue: value?.value,
      filterType: value?.type,
      clickedAt: new Date().toISOString(),
      personaCombination,
    });
    setFilterToggleValue(value);
  };

  const handleLinkClick = (entity, entityId) => {
    trackPersonalisationLinkClick({
      linkId: entityId,
      linkName: entity?.name,
      linkType: entity?.linkType ?? 'link',
      url: entity?.links?.[0]?.url,
      clickedAt: new Date().toISOString(),
      personaCombination,
      personalisationRelevance: entity?.personalisationRelevance,
    });
  };

  const renderResults = () => (
    <div className={c.resultCardsContainer}>
      {renderableSearchResults.map((renderable, i) => {
        if (renderable.renderType === 'single') {
          // Render single 2-row span card
          const { item } = renderable;

          return (
            <ImageSearchResultCardAdapter
              entity={item.entityContent}
              entityId={item.entityId}
              onLinkClick={handleLinkClick}
              key={`entity-id-${item.entityId}`}
            />
          );
        } else {
          // Render pair of cards spanning 1 row each
          const { items } = renderable;
          const isPair = items.length === 2;

          return (
            <div
              className={classNames(c.linkPair, { [c.isPair]: isPair })}
              key={i}
            >
              {items.map((item) => (
                <LinkSearchResultCardAdapter
                  entity={item.entityContent}
                  entityId={item.entityId}
                  onLinkClick={handleLinkClick}
                  key={`entity-id-${item.entityId}`}
                />
              ))}
            </div>
          );
        }
      })}
    </div>
  );

  return (
    <div
      id="servicesPageContainer"
      className={c.pageShell}
      role="main"
      aria-label="Explore"
    >
      <div className={c.page}>
        <PersonalisationModal
          open={showPersonalisationModal}
          onClose={handleClosePersonalisationModal}
          isUpdate={isPersonalisationUpdate}
          existingQuestionResponses={personalisationResponses}
        />
        <SearchBar hasSkipToContent={searchResultCount > 0} />
        {personalisationResponses ? (
          <div className={c.personalisedHeader}>
            <h2 className={fontStyles.heading}>Recommended for you</h2>
            <Tooltip title="Priorities and interests change and that's okay! Answer a few quick questions to personalise your view">
              <Button
                icon={<Icon.ArrowRepeat />}
                iconPosition="right"
                className={c.repersonaliseButton}
                onClick={handleUpdatePersonalisationClick}
              >
                Update personalisation
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div className={c.bannerHeaderContainer}>
            <PersonalisationBanner
              shown={showPersonalisationBanner}
              openPersonalisationModal={openPersonalisationModal}
            />
            <div
              className={classNames(c.unpersonalisedHeader, {
                [c.shown]: !showPersonalisationBanner,
              })}
              inert={showPersonalisationBanner}
            >
              <h2 className={fontStyles.heading}>Explore all</h2>
              <Button
                icon={<Icon.ArrowsMaximize />}
                iconPosition="right"
                className={c.repersonaliseButton}
                onClick={handleShowBanner}
              >
                Personalise my view
              </Button>
            </div>
          </div>
        )}
        <div className={c.mainContent}>
          <div
            className={classNames(c.controls, {
              [c.controlsSolid]: !hasUploadedWallpaper,
            })}
          >
            <div
              className={classNames(c.left, {
                [c.frosted]: hasUploadedWallpaper,
              })}
            >
              <div className={c.toggleWrapper}>
                <Toggle
                  options={FILTER_TOGGLE_OPTIONS}
                  selected={filterToggleValue}
                  setSelected={handleFilterToggleChange}
                />
              </div>
              <div inert className={c.verticalSeparator} />
              <FilterDropdown
                multi
                selectedFilter={categoryFilter}
                setSelectedFilter={(value) => {
                  setCategoryFilter(value);
                }}
                filters={
                  categories?.map((category) => ({
                    text: category.title,
                    value: category.id,
                  })) ?? []
                }
                className={c.dropdown}
                placeholder="Categories"
                align="left"
              />
            </div>
            <div
              className={classNames(c.right, {
                [c.frosted]: hasUploadedWallpaper,
              })}
            >
              <div tabIndex="-1" id="searchResult" role="status">
                {`${searchResultCount} result${
                  searchResultCount !== 1 ? 's' : ''
                }`}
              </div>
              <div inert className={c.verticalSeparator} />
              <FilterDropdown
                selectedFilter={sortType}
                setSelectedFilter={(value) => {
                  setSortType(value);
                }}
                filters={SORT_OPTIONS}
                className={c.dropdown}
              />
            </div>
          </div>
          {loadingLinks && (
            <div className={c.loading}>
              <LoadingIndicator color="var(--canvas-text-color)" />
            </div>
          )}
          {!loadingLinks && searchError && <SearchError />}
          {!loadingLinks && !searchError && (
            <div id="searchResultContainer" tabIndex="-1">
              {renderResults()}
            </div>
          )}
        </div>
      </div>
      <div className={c.background} style={backgroundStyle} />
    </div>
  );
};

export default Services;
